/**
 * TopHeader — Header icons for Help, Chat, and other global features
 *
 * Displays icon buttons in the top-right of the page for:
 * - Help (question mark icon)
 * - Chat (message circle icon)
 * - Notifications (bell icon)
 * - User profile/menu
 *
 * Each icon opens a slide-out panel or dropdown with the relevant content.
 */

import { HelpCircle, Sparkles, Bell, Sun, Moon } from 'lucide-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import ProfileMenu from '../common/ProfileMenu';
import { useThemeStore } from '../../store/themeStore';
import { cn } from '../../lib/utils';

export interface TopHeaderProps {
  className?: string;
  onHelpClick?: () => void;
  onChatClick?: () => void;
  onNotificationsClick?: () => void;
  notificationCount?: number;
  /** Reflects the true open/closed state of the Chat slide-out (owned by MainLayout) */
  chatIsOpen?: boolean;
  helpIsOpen?: boolean;
  notificationsIsOpen?: boolean;
}

export function TopHeader({
  className,
  onHelpClick,
  onChatClick,
  onNotificationsClick,
  notificationCount = 0,
  chatIsOpen = false,
  helpIsOpen = false,
  notificationsIsOpen = false,
}: TopHeaderProps) {
  const { theme, toggleTheme } = useThemeStore();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // Pulse only when the user is on the expanded Help+chat page (/help?chat=open)
  const isExpandedChat = location.pathname === '/help' && searchParams.get('chat') === 'open';

  const handleHelpClick = () => { onHelpClick?.(); };
  const handleChatClick = () => { onChatClick?.(); };
  const handleNotificationsClick = () => { onNotificationsClick?.(); };

  return (
    <div className={cn('flex items-center gap-2 sm:gap-4', className)}>
      {/* AI Chat Button — prominent pill */}
      <button
        onClick={handleChatClick}
        className={cn(
          'relative flex items-center gap-1.5 px-3 py-1.5 rounded-full transition-all duration-200',
          'text-white text-sm font-medium',
          chatIsOpen
            ? 'bg-gradient-to-r from-violet-600 to-indigo-600 shadow-lg shadow-violet-500/30'
            : 'bg-gradient-to-r from-violet-500 to-indigo-500 hover:from-violet-600 hover:to-indigo-600 shadow-md shadow-violet-500/20 hover:shadow-lg hover:shadow-violet-500/30'
        )}
        title="Ask Nabhos"
        aria-label="Open Ask Nabhos"
        aria-pressed={chatIsOpen}
      >
        {/* Pulse ring — only while viewing the expanded Ask Nabhos page */}
        {isExpandedChat && (
          <span className="absolute inset-0 rounded-full animate-ping bg-violet-400 opacity-20 pointer-events-none" />
        )}
        <Sparkles className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="hidden sm:inline">Ask Nabhos</span>
      </button>

      {/* Help Button */}
      <button
        onClick={handleHelpClick}
        className={cn(
          'p-2 rounded-lg transition-all duration-200',
          'text-gray-600 dark:text-gray-400',
          'hover:text-gray-900 dark:hover:text-gray-200',
          'hover:bg-gray-100 dark:hover:bg-metallic-800',
          helpIsOpen && 'bg-gray-100 dark:bg-metallic-800 text-gray-900 dark:text-white'
        )}
        title="Help"
        aria-label="Open help"
        aria-pressed={helpIsOpen}
      >
        <HelpCircle className="w-5 h-5" />
      </button>

      {/* Notifications Button */}
      <div className="relative">
        <button
          onClick={handleNotificationsClick}
          className={cn(
            'relative p-2 rounded-lg transition-all duration-200',
            'text-gray-600 dark:text-gray-400',
            'hover:text-gray-900 dark:hover:text-gray-200',
            'hover:bg-gray-100 dark:hover:bg-metallic-800',
            notificationsIsOpen && 'bg-gray-100 dark:bg-metallic-800 text-gray-900 dark:text-white'
          )}
          title="Notifications"
          aria-label="Open notifications"
          aria-pressed={notificationsIsOpen}
        >
          <Bell className="w-5 h-5" />
        </button>
        {notificationCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold leading-none text-white bg-red-600 rounded-full">
            {notificationCount > 99 ? '99+' : notificationCount}
          </span>
        )}
      </div>

      {/* Theme Toggle */}
      <button
        onClick={toggleTheme}
        className={cn(
          'p-2 rounded-lg transition-all duration-200',
          'text-gray-600 dark:text-gray-400',
          'hover:text-gray-900 dark:hover:text-gray-200',
          'hover:bg-gray-100 dark:hover:bg-metallic-800'
        )}
        title={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
        aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        {theme === 'dark' ? (
          <Sun className="w-5 h-5" />
        ) : (
          <Moon className="w-5 h-5" />
        )}
      </button>

      {/* Divider */}
      <div className="w-px h-6 bg-gray-200 dark:bg-metallic-800 mx-1" />

      {/* Profile Menu */}
      <ProfileMenu />
    </div>
  );
}

export default TopHeader;

// Made with Bob
